/**
 * Story 30.3 — TanStack hooks and actions for "This device" and the device
 * list on Settings › Notifications, plus the boot sync.
 *
 *   ['push-config']                   GET /api/push/config (fetched on page
 *                                     load — never inside the toggle's click)
 *   ['push-subscriptions', tenantId]  GET /api/push/subscriptions (my devices)
 *   ['push-this-device']              sha256 of THIS browser's endpoint, or null
 *
 * "On" = this browser has a subscription whose hash is in my server list.
 *
 * 🔴 `turnOn()` calls `Notification.requestPermission()` as its FIRST
 * statement, before any `await`: Safari only prompts from inside the user
 * gesture, and an await first would lose it. The click handler must call
 * `turnOn()` synchronously.
 *
 * Off: DELETE the row, then `unsubscribe()`. Removing THIS device from the
 * list does the same.
 *
 * Boot sync (`syncThisDevice`, run once per signed-in shell by
 * `PushDeviceSync`): with permission granted and a subscription present —
 * a different VAPID key (rotation) → unsubscribe, re-subscribe (no prompt;
 * permission is already granted) and POST with `replacesEndpoint`; a hash my
 * server list lacks (another user re-homed it, or I removed it elsewhere) →
 * unsubscribe (ruling O14). Errors are swallowed (console only, no endpoint).
 */

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deletePushSubscription,
  fetchPushConfig,
  fetchPushSubscriptions,
  postPushSubscription,
  type PushConfig,
  type PushDevice,
} from '@/lib/api/pushSubscriptions';
import { useAuth } from '@/lib/contexts/AuthContext';
import { pushSupport, type PushSupport } from './support';
import {
  currentSubscription,
  endpointSha256,
  hasApplicationServerKey,
  subscribeBody,
  subscribeThisDevice,
  unsubscribeQuietly,
} from './subscribe';

export const PUSH_CONFIG_QUERY_KEY = ['push-config'] as const;
export const PUSH_SUBSCRIPTIONS_QUERY_KEY = ['push-subscriptions'] as const;
export const PUSH_THIS_DEVICE_QUERY_KEY = ['push-this-device'] as const;

export type PushPermission = NotificationPermission | 'unavailable';

export function readPermission(): PushPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unavailable';
  return Notification.permission;
}

export function usePushConfig() {
  return useQuery<PushConfig, Error>({
    queryKey: PUSH_CONFIG_QUERY_KEY,
    queryFn: ({ signal }) => fetchPushConfig(signal),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function usePushSubscriptions() {
  const { tenant } = useAuth();
  const tenantId = tenant?.id ?? null;
  return useQuery<PushDevice[], Error>({
    queryKey: [...PUSH_SUBSCRIPTIONS_QUERY_KEY, tenantId],
    queryFn: ({ signal }) => fetchPushSubscriptions(signal),
    enabled: tenantId !== null,
    staleTime: 60_000,
    retry: false,
  });
}

async function thisDeviceHash(): Promise<string | null> {
  const subscription = await currentSubscription();
  return subscription ? endpointSha256(subscription.endpoint) : null;
}

export function useThisDeviceHash(enabled: boolean) {
  return useQuery<string | null, Error>({
    queryKey: PUSH_THIS_DEVICE_QUERY_KEY,
    queryFn: thisDeviceHash,
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}

export type TurnOnOutcome = 'on' | 'denied' | 'dismissed' | 'unavailable';

/**
 * Subscribe (or reuse a current-key subscription) and save it. A
 * subscription made with another VAPID key is replaced.
 */
/**
 * The turn-on save in flight, if any. The boot sync waits for it before it
 * judges a subscription "unlisted": the subscribe can resolve before the POST
 * lands, and a device list fetched before the POST would not hold it yet.
 */
let saveInFlight: Promise<void> | null = null;

function subscribeAndSave(publicKey: string): Promise<void> {
  const save = doSubscribeAndSave(publicKey);
  saveInFlight = save;
  const clear = () => {
    if (saveInFlight === save) saveInFlight = null;
  };
  save.then(clear, clear);
  return save;
}

async function doSubscribeAndSave(publicKey: string): Promise<void> {
  let existing = await currentSubscription();
  let replacesEndpoint: string | undefined;
  if (existing && !hasApplicationServerKey(existing, publicKey)) {
    replacesEndpoint = existing.endpoint;
    await unsubscribeQuietly(existing);
    existing = null;
  }
  const subscription = existing ?? (await subscribeThisDevice(publicKey));
  await postPushSubscription(subscribeBody(subscription, replacesEndpoint));
}

export interface SyncThisDeviceInput {
  config: PushConfig;
  devices: PushDevice[];
  permission?: PushPermission;
}

/** The boot sync (see the header). Never throws. Returns what it did. */
export async function syncThisDevice(
  input: SyncThisDeviceInput,
): Promise<'none' | 'rotated' | 'unlisted'> {
  try {
    const permission = input.permission ?? readPermission();
    if (permission !== 'granted' || !input.config.available || !input.config.publicKey) return 'none';
    const subscription = await currentSubscription();
    if (!subscription) return 'none';
    const hash = await endpointSha256(subscription.endpoint);
    const listed = input.devices.some((d) => d.endpointSha256 === hash);
    if (!listed) {
      // The list may predate a turn-on that is still saving (or just saved):
      // wait for that save, then ask the server again before unsubscribing.
      if (saveInFlight) await saveInFlight.catch(() => undefined);
      const fresh = await fetchPushSubscriptions();
      if (fresh.some((d) => d.endpointSha256 === hash)) return 'none';
      await unsubscribeQuietly(subscription);
      return 'unlisted';
    }
    if (!hasApplicationServerKey(subscription, input.config.publicKey)) {
      const oldEndpoint = subscription.endpoint;
      await unsubscribeQuietly(subscription);
      const fresh = await subscribeThisDevice(input.config.publicKey);
      await postPushSubscription(subscribeBody(fresh, oldEndpoint));
      return 'rotated';
    }
    return 'none';
  } catch (err) {
    console.warn('push: device sync failed', err instanceof Error ? err.message : err);
    return 'none';
  }
}

export interface DevicePush {
  support: PushSupport;
  permission: PushPermission;
  config: PushConfig | undefined;
  configLoading: boolean;
  configError: boolean;
  devices: PushDevice[] | undefined;
  devicesLoading: boolean;
  devicesError: boolean;
  thisDeviceHash: string | null;
  /** This browser has a subscription that my server list contains. */
  on: boolean;
  busy: boolean;
  error: string | null;
  /** 🔴 Call synchronously from the click handler (see the header). */
  turnOn: () => Promise<TurnOnOutcome>;
  turnOff: () => Promise<void>;
  removeDevice: (id: string) => void;
  removingId: string | null;
}

export function useDevicePush(): DevicePush {
  const queryClient = useQueryClient();
  const [support] = useState<PushSupport>(() => pushSupport());
  const [permission, setPermission] = useState<PushPermission>(() => readPermission());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const config = usePushConfig();
  const devices = usePushSubscriptions();
  const hash = useThisDeviceHash(support === 'supported');
  const thisDeviceHash = hash.data ?? null;
  const on = thisDeviceHash !== null && (devices.data ?? []).some((d) => d.endpointSha256 === thisDeviceHash);

  const refresh = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: PUSH_THIS_DEVICE_QUERY_KEY }),
      queryClient.invalidateQueries({ queryKey: PUSH_SUBSCRIPTIONS_QUERY_KEY }),
    ]);
  }, [queryClient]);

  const publicKey = config.data?.available ? config.data.publicKey : null;

  const turnOn = useCallback((): Promise<TurnOnOutcome> => {
    // FIRST statement, before any await: keeps the user gesture for Safari.
    const permissionRequest: Promise<NotificationPermission> =
      typeof window !== 'undefined' && 'Notification' in window
        ? Notification.requestPermission()
        : Promise.resolve('denied');
    return (async (): Promise<TurnOnOutcome> => {
      setError(null);
      setBusy(true);
      try {
        const result = await permissionRequest;
        setPermission(result);
        if (result === 'denied') return 'denied';
        if (result !== 'granted') return 'dismissed';
        if (!publicKey) return 'unavailable';
        await subscribeAndSave(publicKey);
        await refresh();
        return 'on';
      } catch (err) {
        console.warn('push: could not turn on notifications', err instanceof Error ? err.message : err);
        setError('Notifications could not be turned on for this device. Try again in a moment.');
        await refresh().catch(() => undefined);
        return 'unavailable';
      } finally {
        setBusy(false);
      }
    })();
  }, [publicKey, refresh]);

  const turnOff = useCallback(async () => {
    setError(null);
    setBusy(true);
    try {
      const subscription = await currentSubscription();
      const mine = (devices.data ?? []).find((d) => d.endpointSha256 === thisDeviceHash);
      if (mine) await deletePushSubscription(mine.id);
      await unsubscribeQuietly(subscription);
      await refresh();
    } catch (err) {
      console.warn('push: could not turn off notifications', err instanceof Error ? err.message : err);
      setError('Notifications could not be turned off for this device. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }, [devices.data, thisDeviceHash, refresh]);

  const remove = useMutation<void, Error, string>({
    mutationFn: async (id) => {
      const isThis = (devices.data ?? []).some((d) => d.id === id && d.endpointSha256 === thisDeviceHash);
      await deletePushSubscription(id);
      if (isThis) await unsubscribeQuietly(await currentSubscription());
    },
    onSettled: () => refresh(),
  });

  return {
    support,
    permission,
    config: config.data,
    configLoading: config.isPending,
    configError: config.isError,
    devices: devices.data,
    devicesLoading: devices.isPending,
    devicesError: devices.isError,
    thisDeviceHash,
    on,
    busy,
    error: error ?? (remove.isError ? 'That device could not be removed. Try again in a moment.' : null),
    turnOn,
    turnOff,
    removeDevice: (id) => remove.mutate(id),
    removingId: remove.isPending ? (remove.variables ?? null) : null,
  };
}
